import React from "react";
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from "date-fns";
import { safeParseISO } from "@/utils";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Edit, Trash2 } from "lucide-react";

function getEventStartDate(evento) {
  const raw = evento.data_inicio ?? evento.dataInicio;
  if (raw == null || (typeof raw === "string" && !raw.trim())) return null;
  try {
    const d = typeof raw === "string" ? safeParseISO(raw) : new Date(raw);
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

export default function VisaoSemana({ selectedDate, eventos, onEdit, onDelete, getEventColorClass, getStatusBadge }) {
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Segunda-feira
  const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
  const allDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
  // Filtrar apenas dias úteis (segunda a sexta)
  const daysOfWeek = allDays.filter(day => {
    const dayOfWeek = day.getDay();
    return dayOfWeek >= 1 && dayOfWeek <= 5;
  });

  const getEventosForDate = (date) => {
    return eventos.filter((evento) => {
      const eventoDate = getEventStartDate(evento);
      return eventoDate != null && isSameDay(eventoDate, date);
    }).sort((a, b) => {
      const da = getEventStartDate(a);
      const db = getEventStartDate(b);
      if (da == null || db == null) return 0;
      return da.getTime() - db.getTime();
    });
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-slate-900">
          Semana de {format(weekStart, "dd/MM", { locale: ptBR })} a {format(weekEnd, "dd/MM/yyyy", { locale: ptBR })}
        </h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {daysOfWeek.map((day) => {
          const dayEventos = getEventosForDate(day);
          const isToday = isSameDay(day, new Date());
          const isSelected = isSameDay(day, selectedDate);

          return (
            <Card 
              key={day.toISOString()} 
              className={`min-h-[200px] ${isSelected ? 'ring-2 ring-blue-500' : ''} ${isToday ? 'bg-blue-50' : ''}`}
            >
              <CardHeader className="p-3 pb-2 border-b">
                <CardTitle className="text-sm font-semibold text-center">
                  <div className={`${isToday ? 'text-blue-600 font-bold' : 'text-slate-700'}`}>
                    {format(day, "EEE", { locale: ptBR })}
                  </div>
                  <div className={`text-lg ${isToday ? 'text-blue-600 font-bold' : ''}`}>
                    {format(day, "dd")}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 space-y-2">
                {dayEventos.length === 0 ? (
                  <p className="text-xs text-center text-slate-400 py-4">Sem eventos</p>
                ) : (
                  dayEventos.map((evento) => {
                    const statusBadge = getStatusBadge(evento.status);
                    return (
                      <div
                        key={evento.id}
                        className={`p-2 rounded-lg border-l-4 ${getEventColorClass(evento.tipo_evento)} bg-white hover:shadow-md transition-shadow cursor-pointer group`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-900 truncate">{evento.titulo}</p>
                            <div className="flex items-center gap-1 text-xs text-slate-600 mt-0.5">
                              <Clock className="w-3 h-3" />
                              <span>{getEventStartDate(evento) ? format(getEventStartDate(evento), "HH:mm") : "—"}</span>
                            </div>
                            <Badge className={`${statusBadge.className} text-[10px] mt-1 px-1 py-0`}>
                              {statusBadge.label}
                            </Badge>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(evento);
                              }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-600 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(evento);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}